import { log } from '@/log'
import { RawTag } from '@/models/api/tags'
import { ApiClientWithAuth } from '@/services/api/client'
import { ApiService } from '@/services/api/service'
import { be, join, largerThanOrEqual, notNil, requireKeys, union, validDate } from '@/util/parse-object'
import { Either, left, right } from 'fp-ts/lib/Either'
import { none, Option, some } from 'fp-ts/lib/Option'

/**
 * TODO: {@see User}と統合. store専用の型定義を廃止して、{@see CurrentUserScheme}に寄せるで良さそう
 */
export interface CurrentUserScheme {
  id: string
  shortid: string
  first_name?: string
  last_name?: string
  phonetic_first_name?: string
  phonetic_last_name?: string
  birthday?: string
  gender: number
  email?: string
  tel?: string
  zipcode?: string
  region?: string
  address1?: string
  address2?: string
  current_point?: number
  aux_text_10: string
  user_status: CurrentUserStatus
  payment_auth_token?: string
  payment_accountid?: string
  elapsed_days_last_order?: number,
  order_times?: number,
  created_at: string
  tags: RawTag[]
  membership: Membership
  /**
   * 注文集計データ
   *
   * @description {@link OrderAggregationScheme}形式のJSON文字列. 初訪問ユーザーなど、集計データそのものが存在しないケースもある.
   *
   * @see https://github.com/my-color/front/issues/7268
   */
  readonly aux_string_3?: string | null
  /**
   * 招待情報
   * @see https://github.com/Everforth/growthpalette-pm/issues/304#issuecomment-2443116320
   */
  readonly aux_string_4?: string
}
export type CurrentUserStatus = 'registered' | 'provisional'

export type OrderAggregationScheme = InitialOrderAggregationScheme | StoredOrderAggregationScheme
/**
 * 確定済み注文データが存在しない、初期状態のケース
 * @see https://github.com/my-color/front/issues/7268
 */
interface InitialOrderAggregationScheme {
  /*
   * - 別サイトから移管する時は、旧サイトの購入履歴情報をバッチで集計し、各ユーザーに書き込む。
   *     - このケースを考慮しなくてはならないので、indexsummaryは頼れない
   * - GPの別環境に導入する時は、導入時点の購入履歴情報をバッチで集計し、各ユーザーに書き込む想定（最終的に正義なのは、購入履歴情報の集計値なので）
   */
  readonly last_purchased_date: null
  readonly last_purchased_amount: null
  readonly total_purchased_count: 0
  readonly total_purchased_amount: 0
}
/**
 * 確定済み注文データが存在するケース
 */
interface StoredOrderAggregationScheme {
  readonly last_purchased_date: string
  readonly last_purchased_amount: number
  readonly total_purchased_count: number
  readonly total_purchased_amount: number
}
export function parseOrderAggregation(
  user: Pick<CurrentUserScheme, 'aux_string_3'>
): Either<Error[], Option<OrderAggregationScheme>> {
  if (!user.aux_string_3) {
    return right(none)
  }

  const parser = (() => {
    const requiredKeys = requireKeys([
      'last_purchased_amount',
      'last_purchased_date',
      'total_purchased_count',
      'total_purchased_amount',
    ])

    return union<OrderAggregationScheme>(
      join<InitialOrderAggregationScheme>(
        requiredKeys,
        be(['last_purchased_amount', 'last_purchased_date'], null),
        be(['total_purchased_count', 'total_purchased_amount'], 0)
      ),
      join<StoredOrderAggregationScheme>(
        requiredKeys,
        notNil([
          'last_purchased_amount',
          'last_purchased_date',
          'total_purchased_count',
          'total_purchased_amount',
        ]),
        largerThanOrEqual(['total_purchased_count', 'total_purchased_amount', 'last_purchased_amount'], 0),
        validDate(['last_purchased_date'])
      )
    )
  })()

  try {
    const obj = JSON.parse(user.aux_string_3)

    return parser(obj).map(some)
  } catch (e) {
    return left([e])
  }
}

export interface Membership {
  enabled: boolean
  registered_plans: Array<{ external_id: string, display_label: string }>
  remaining_count: RemainingCount|null
}

export interface RemainingCount {
  purchase_not_considering_current_cart: number,
  add_to_cart: number,
}

const namespace = 'current_user'

export class CurrentUser extends ApiService {
  async getCurrentUser(): Promise<Either<Error, CurrentUserScheme>> {
    return ApiClientWithAuth.get('/api/user/current_user')
      .then((response): Either<Error, CurrentUserScheme> => {
        log.debug({ service: `${namespace}/getCurrentUser`, response })

        return right(insertMockMembership(response.body.data))
      })
      .catch((e): Either<Error, CurrentUserScheme> => {
        log.error({ service: `${namespace}/getCurrentUser` }, e)

        return left(e)
      })
  }
}

/**
 * @deprecated apiがデータを返すようになったら削除
 * @param data
 */
function insertMockMembership(data: any): CurrentUserScheme {
  return {
    ...data,
    membership: data.membership || {
      enabled: true,
      registered_plans: [
        {
          external_id: 'womenz-1m',
          display_label: 'レディース一ヶ月',
        },
        {
          external_id: 'menz-1m',
          display_label: 'メンズ一ヶ月',
        },
      ],
    },
  }
}
